(() => {
  class ConcentrixHero {
    constructor(element) {
      this.element = element;
      this.slides = [...element.querySelectorAll('[data-cx-slide]')];
      this.dots = [...element.querySelectorAll('[data-cx-dot]')];
      this.prevButton = element.querySelector('[data-cx-prev]');
      this.nextButton = element.querySelector('[data-cx-next]');
      this.delay = Number(element.dataset.autoplay || 5000);
      this.index = 0;
      this.timer = null;

      if (this.slides.length < 2) return;

      this.prevButton?.addEventListener('click', () => this.show(this.index - 1, true));
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
      this.dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => this.show(dotIndex, true));
      });

      this.element.addEventListener('mouseenter', () => this.stop());
      this.element.addEventListener('mouseleave', () => this.start());

      this.show(0);
      this.start();
    }

    show(nextIndex, userTriggered = false) {
      if (!this.slides.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === this.index);
      });

      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === this.index);
      });

      if (userTriggered) this.start();
    }

    start() {
      this.stop();
      if (this.slides.length < 2) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  function parseRgbColor(value) {
    if (!value || value === 'transparent') return null;
    const match = value.match(/^rgba?\((.+)\)$/);
    if (!match) return null;

    const parts = match[1]
      .replace(/\//g, ' ')
      .split(/[,\s]+/)
      .filter(Boolean);
    if (parts.length < 3) return null;

    const color = {
      r: Number.parseFloat(parts[0]),
      g: Number.parseFloat(parts[1]),
      b: Number.parseFloat(parts[2]),
      a: parts[3] === undefined ? 1 : Number.parseFloat(parts[3]),
    };

    if ([color.r, color.g, color.b, color.a].some((part) => Number.isNaN(part))) return null;
    return color.a > 0 ? color : null;
  }

  function getRelativeLuminance(color) {
    const [r, g, b] = [color.r, color.g, color.b].map((channel) => {
      const value = channel / 255;
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function hasVisualBackground(element) {
    const style = window.getComputedStyle(element);
    if (style.backgroundImage && style.backgroundImage !== 'none') return true;

    const afterStyle = window.getComputedStyle(element, '::after');
    if (afterStyle.backgroundImage && afterStyle.backgroundImage !== 'none') return true;

    return ['CANVAS', 'IMG', 'PICTURE', 'VIDEO'].includes(element.tagName);
  }

  function getElementBackgroundColor(element) {
    return parseRgbColor(window.getComputedStyle(element).backgroundColor);
  }

  function findBackgroundColorFromNode(node) {
    let current = node;
    let hasMediaBackdrop = false;

    while (current && current !== document.body && current !== document.documentElement) {
      const backgroundColor = getElementBackgroundColor(current);
      if (backgroundColor) return { color: backgroundColor, hasMediaBackdrop };

      if (hasVisualBackground(current)) hasMediaBackdrop = true;
      current = current.parentElement;
    }

    return { color: null, hasMediaBackdrop };
  }

  function getBrochureLinkBackdropColor(link) {
    const rect = link.getBoundingClientRect();
    const inset = Math.min(16, rect.width / 3);
    const points = [
      [rect.left + rect.width / 2, rect.top + rect.height / 2],
      [rect.left + inset, rect.top + rect.height / 2],
      [rect.right - inset, rect.top + rect.height / 2],
    ];
    let foundMediaBackdrop = false;

    for (const [x, y] of points) {
      const elements = document.elementsFromPoint(x, y);

      for (const element of elements) {
        if (!(element instanceof Element)) continue;
        if (element === link || link.contains(element)) continue;
        if (element.closest('[data-cx-brochure-contrast], .section-header, .header-wrapper, .header')) continue;

        const result = findBackgroundColorFromNode(element);
        if (result.color) return result.color;
        if (result.hasMediaBackdrop) foundMediaBackdrop = true;
      }
    }

    if (foundMediaBackdrop) return null;
    return getElementBackgroundColor(document.body) || getElementBackgroundColor(document.documentElement);
  }

  function updateBrochureLinkContrast(link) {
    const backdropColor = getBrochureLinkBackdropColor(link);
    const isOnLight = backdropColor ? getRelativeLuminance(backdropColor) > 0.62 : false;

    link.classList.toggle('is-on-light', isOnLight);
  }

  function initBrochureLinkContrast(scope = document) {
    const links = scope.matches?.('[data-cx-brochure-contrast]')
      ? [scope]
      : [...scope.querySelectorAll('[data-cx-brochure-contrast]')];

    links.forEach((link) => {
      let frame = 0;
      const scheduleUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          updateBrochureLinkContrast(link);
        });
      };

      if (link.dataset.cxBound === 'true') {
        scheduleUpdate();
        return;
      }

      link.dataset.cxBound = 'true';
      scheduleUpdate();
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate);
    });
  }

  function initLoadMore(scope = document) {
    scope.querySelectorAll('[data-cx-loadmore]').forEach((button) => {
      if (button.dataset.cxBound === 'true') return;
      button.dataset.cxBound = 'true';

      button.addEventListener('click', async (event) => {
        event.preventDefault();

        const nextUrl = button.getAttribute('href');
        const targetSelector = button.dataset.target;
        if (!nextUrl || !targetSelector) return;

        button.classList.add('is-loading');

        try {
          const response = await fetch(nextUrl);
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const currentTarget = document.querySelector(targetSelector);
          const incomingTarget = documentFragment.querySelector(targetSelector);
          const incomingButton = documentFragment.querySelector('[data-cx-loadmore]');

          if (currentTarget && incomingTarget) {
            [...incomingTarget.children].forEach((node) => currentTarget.appendChild(node));
          }

          if (incomingButton?.getAttribute('href')) {
            button.setAttribute('href', incomingButton.getAttribute('href'));
          } else {
            button.remove();
          }
        } catch (error) {
          console.error('Concentrix load more failed', error);
          button.classList.remove('is-loading');
        } finally {
          button.classList.remove('is-loading');
        }
      });
    });
  }

  function initContactShell(scope = document) {
    scope.querySelectorAll('[data-cx-contact-shell]').forEach((shell) => {
      if (shell.dataset.cxBound === 'true') return;
      shell.dataset.cxBound = 'true';

      const dialog = shell.querySelector('[data-cx-contact-dialog]');
      const openDialog = () => {
        if (!dialog) return;
        dialog.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      };
      const closeDialog = () => {
        if (!dialog) return;
        dialog.hidden = true;
        document.documentElement.style.overflow = '';
      };

      shell.querySelectorAll('[data-cx-dialog-close]').forEach((el) => {
        el.addEventListener('click', closeDialog);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && dialog && !dialog.hidden) closeDialog();
      });

      const dropzone = shell.querySelector('[data-cx-dropzone]');
      const fileInput = shell.querySelector('[data-cx-file-input]');
      const fileList = shell.querySelector('[data-cx-file-list]');

      const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
      };

      const getFileKind = (file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (file.type.startsWith('image/')) return 'image';
        if (ext === 'ppt' || ext === 'pptx') return 'ppt';
        if (ext === 'xls' || ext === 'xlsx') return 'xls';
        if (ext === 'doc' || ext === 'docx') return 'doc';
        if (ext === 'zip') return 'zip';
        return 'etc';
      };

      const iconConfigEl = shell.querySelector('[data-cx-contact-form]');
      const customIconUrls = {
        doc: (iconConfigEl?.dataset.cxIconDoc || '').trim(),
        xls: (iconConfigEl?.dataset.cxIconXls || '').trim(),
        ppt: (iconConfigEl?.dataset.cxIconPpt || '').trim(),
        zip: (iconConfigEl?.dataset.cxIconZip || '').trim(),
      };

      const createFileCard = (file) => {
        const card = document.createElement('div');
        card.className = 'cx-contact-form__file-list-item';

        const content = document.createElement('div');
        content.className = 'cx-contact-form__file-list-item-content';

        const icon = document.createElement('div');
        icon.className = 'cx-contact-form__file-icon';
        const kind = getFileKind(file);
        const customIcon = customIconUrls[kind];
        if (kind === 'image') {
          const img = document.createElement('img');
          img.alt = '';
          img.src = URL.createObjectURL(file);
          img.addEventListener('load', () => URL.revokeObjectURL(img.src), { once: true });
          icon.appendChild(img);
        } else if (customIcon) {
          const img = document.createElement('img');
          img.alt = '';
          img.src = customIcon;
          icon.appendChild(img);
        } else {
          icon.classList.add('cx-contact-form__file-icon--badge');
          icon.textContent = 'etc';
        }

        const meta = document.createElement('div');
        meta.className = 'cx-contact-form__file-meta';

        const name = document.createElement('p');
        name.className = 'cx-contact-form__file-name';
        name.textContent = file.name;
        name.title = file.name;

        const size = document.createElement('p');
        size.className = 'cx-contact-form__file-size';
        size.textContent = formatFileSize(file.size);

        meta.appendChild(name);
        meta.appendChild(size);
        content.appendChild(icon);
        content.appendChild(meta);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cx-contact-form__file-remove';
        remove.setAttribute('aria-label', '파일 제거');
        remove.innerHTML = '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true"><path d="M2 2l7 7M9 2L2 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
        remove.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          removeFile(file);
        });

        card.appendChild(content);
        card.appendChild(remove);
        return card;
      };

      const renderFileList = () => {
        if (!fileList) return;
        const files = fileInput?.files ? [...fileInput.files] : [];
        fileList.innerHTML = '';
        if (!files.length) {
          fileList.hidden = true;
          return;
        }
        fileList.hidden = false;
        files.forEach((file) => {
          fileList.appendChild(createFileCard(file));
        });
      };

      const setFiles = (files) => {
        if (!fileInput) return;
        const dt = new DataTransfer();
        [...files].slice(0, 5).forEach((f) => dt.items.add(f));
        fileInput.files = dt.files;
        renderFileList();
      };

      const removeFile = (fileRef) => {
        if (!fileInput) return;
        const dt = new DataTransfer();
        [...fileInput.files].filter((f) => f !== fileRef).forEach((f) => dt.items.add(f));
        fileInput.files = dt.files;
        renderFileList();
      };

      if (dropzone && fileInput) {
        fileInput.addEventListener('change', () => renderFileList());

        if (fileList) {
          fileList.addEventListener('click', (event) => {
            if (event.target.closest('.cx-contact-form__file-remove')) return;
            event.preventDefault();
          });
        }

        ['dragenter', 'dragover'].forEach((evt) => {
          dropzone.addEventListener(evt, (event) => {
            event.preventDefault();
            dropzone.classList.add('is-drag');
          });
        });
        ['dragleave', 'drop'].forEach((evt) => {
          dropzone.addEventListener(evt, (event) => {
            event.preventDefault();
            dropzone.classList.remove('is-drag');
          });
        });
        dropzone.addEventListener('drop', (event) => {
          const dt = event.dataTransfer;
          if (!dt || !dt.files?.length) return;
          setFiles(dt.files);
        });
      }

      const setFieldError = (field, message) => {
        if (!field) return;
        field.classList.add('is-invalid');
        const errorEl = field.querySelector('[data-cx-error]');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
      };

      const clearFieldError = (field) => {
        if (!field) return;
        field.classList.remove('is-invalid');
        const errorEl = field.querySelector('[data-cx-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      };

      shell.querySelectorAll('[data-cx-select]').forEach((select) => {
        const trigger = select.querySelector('[data-cx-select-trigger]');
        const menu = select.querySelector('[data-cx-select-menu]');
        const valueEl = select.querySelector('[data-cx-select-value]');
        const hiddenInput = select.querySelector('[data-cx-select-input]');
        const options = [...select.querySelectorAll('[data-cx-select-option]')];
        const fieldEl = select.closest('.cx-contact-form__field');

        const close = () => {
          select.classList.remove('is-open');
          menu.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        };

        const open = () => {
          shell.querySelectorAll('[data-cx-select].is-open').forEach((other) => {
            if (other !== select) {
              other.classList.remove('is-open');
              other.querySelector('[data-cx-select-menu]').hidden = true;
              other.querySelector('[data-cx-select-trigger]').setAttribute('aria-expanded', 'false');
            }
          });
          select.classList.add('is-open');
          menu.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        };

        trigger.addEventListener('click', () => {
          if (select.classList.contains('is-open')) close();
          else open();
        });

        options.forEach((option) => {
          option.addEventListener('click', () => {
            options.forEach((o) => o.classList.remove('is-selected'));
            option.classList.add('is-selected');
            hiddenInput.value = option.dataset.value;
            valueEl.textContent = option.querySelector('.cx-select__option-label')?.textContent.trim() || '';
            valueEl.classList.remove('is-placeholder');
            clearFieldError(fieldEl);
            close();
          });
        });

        document.addEventListener('click', (event) => {
          if (!select.contains(event.target)) close();
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && select.classList.contains('is-open')) close();
        });
      });

      const form = shell.querySelector('[data-cx-contact-form]');
      if (form) {
        const isEN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
        const messages = isEN
          ? {
              nameRequired: 'Please enter your name.',
              categoryRequired: 'Please select an inquiry type.',
              emailRequired: 'Please enter your email.',
              emailFormat: 'Please enter a valid email address.',
              descriptionRequired: 'Please enter your inquiry.',
              submitFailed: 'Failed to send your inquiry. Please try again later.',
            }
          : {
              nameRequired: '이름은 필수입니다.',
              categoryRequired: '문의 유형을 선택해 주세요.',
              emailRequired: '이메일은 필수입니다.',
              emailFormat: '올바른 이메일 형식이 아닙니다.',
              descriptionRequired: '문의 내용은 필수입니다.',
              submitFailed: '문의 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.',
            };

        const fieldRules = [
          { name: 'name', requiredMessage: messages.nameRequired },
          { name: 'category', requiredMessage: messages.categoryRequired },
          { name: 'email', requiredMessage: messages.emailRequired, formatMessage: messages.emailFormat, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
          { name: 'description', requiredMessage: messages.descriptionRequired },
        ];

        const getFieldEl = (name) => form.querySelector(`[data-cx-field="${name}"]`);

        const validateForm = () => {
          let valid = true;
          let firstInvalid = null;
          fieldRules.forEach((rule) => {
            const fieldEl = getFieldEl(rule.name);
            if (!fieldEl) return;
            const value = (new FormData(form).get(rule.name) || '').toString().trim();
            if (!value) {
              setFieldError(fieldEl, rule.requiredMessage);
              valid = false;
              if (!firstInvalid) firstInvalid = fieldEl;
              return;
            }
            if (rule.pattern && !rule.pattern.test(value)) {
              setFieldError(fieldEl, rule.formatMessage);
              valid = false;
              if (!firstInvalid) firstInvalid = fieldEl;
              return;
            }
            clearFieldError(fieldEl);
          });
          if (firstInvalid) {
            const focusTarget = firstInvalid.querySelector('input, textarea, [data-cx-select-trigger]');
            focusTarget?.focus();
          }
          return valid;
        };

        form.addEventListener('input', (event) => {
          const target = event.target;
          if (target.matches('input, textarea')) {
            clearFieldError(target.closest('.cx-contact-form__field'));
          }
        });

        const resetForm = () => {
          form.reset();
          form.querySelectorAll('.cx-contact-form__field').forEach(clearFieldError);
          form.querySelectorAll('[data-cx-select]').forEach((select) => {
            select.querySelectorAll('[data-cx-select-option]').forEach((o) => o.classList.remove('is-selected'));
            const valueEl = select.querySelector('[data-cx-select-value]');
            const hiddenInput = select.querySelector('[data-cx-select-input]');
            if (valueEl && hiddenInput) {
              hiddenInput.value = '';
              valueEl.textContent = valueEl.dataset.placeholder || '';
              valueEl.classList.add('is-placeholder');
            }
          });
          renderFileList();
        };

        const submitButton = form.querySelector('.cx-contact-form__submit');

        const EXT_CONTENT_TYPE = {
          pdf: 'application/pdf',
          zip: 'application/zip',
          jpg: 'image/jpeg',
          jpeg: 'image/jpeg',
          png: 'image/png',
          gif: 'image/gif',
          webp: 'image/webp',
          svg: 'image/svg+xml',
          doc: 'application/msword',
          docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ppt: 'application/vnd.ms-powerpoint',
          pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          xls: 'application/vnd.ms-excel',
          xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          txt: 'text/plain',
          csv: 'text/csv',
        };

        const detectContentType = (file) => {
          if (file.type) return file.type;
          const ext = (file.name.split('.').pop() || '').toLowerCase();
          return EXT_CONTENT_TYPE[ext] || '';
        };

        const uploadFilesViaPresigned = async (files, presignedEndpoint) => {
          const manifest = files.map((file) => {
            const entry = { filename: file.name, size: file.size };
            const ct = detectContentType(file);
            if (ct) entry.contentType = ct;
            return entry;
          });

          const presignRes = await fetch(presignedEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ files: manifest }),
          });
          if (!presignRes.ok) throw new Error(`Presign responded ${presignRes.status}`);
          const presignData = await presignRes.json();
          if (!presignData?.ok || !Array.isArray(presignData.files)) {
            throw new Error('Invalid presign response');
          }

          await Promise.all(
            presignData.files.map((entry, index) => {
              const file = files[index];
              const headers = {};
              if (entry.contentType) headers['Content-Type'] = entry.contentType;
              return fetch(entry.uploadUrl, { method: 'PUT', headers, body: file }).then((res) => {
                if (!res.ok) throw new Error(`Upload failed for ${entry.filename} (${res.status})`);
              });
            }),
          );

          return presignData.files.map((entry) => entry.key);
        };

        form.addEventListener('submit', async (event) => {
          event.preventDefault();

          if (!validateForm()) {
            return;
          }

          const useLambda = form.dataset.cxUseLambda === 'true';
          const endpoint = (form.dataset.cxLambdaEndpoint || '').trim();
          const presignedEndpoint = (form.dataset.cxPresignedEndpoint || '').trim();
          const cleanupEndpoint = (form.dataset.cxCleanupEndpoint || '').trim();

          // Mock 성공(폼 리셋 + 완료 다이얼로그)은 테마 에디터에서만 허용한다.
          // 실제 스토어에서 엔드포인트가 비어 있으면 문의가 유실되므로 실패로 처리한다.
          if (!useLambda || !endpoint) {
            if (window.Shopify?.designMode) {
              resetForm();
              openDialog();
              return;
            }
            console.error('[cx-contact] Lambda endpoint is not configured; inquiry was not sent.');
            alert(messages.submitFailed);
            return;
          }

          submitButton?.setAttribute('disabled', 'true');
          let uploadedKeys = [];
          try {
            const formData = new FormData(form);
            const attachedFiles = fileInput?.files ? [...fileInput.files] : [];

            if (attachedFiles.length) {
              if (!presignedEndpoint) throw new Error('Presigned endpoint is not configured; attachments cannot be uploaded.');
              uploadedKeys = await uploadFilesViaPresigned(attachedFiles, presignedEndpoint);
            }

            const payload = {
              name: (formData.get('name') || '').toString(),
              company: (formData.get('company') || '').toString(),
              category: (formData.get('category') || '').toString(),
              email: (formData.get('email') || '').toString(),
              description: (formData.get('description') || '').toString(),
              files: uploadedKeys,
            };

            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Lambda responded ${response.status}`);
          } catch (error) {
            console.error('[cx-contact] Lambda submit failed', error);
            if (uploadedKeys.length && cleanupEndpoint) {
              fetch(cleanupEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ files: uploadedKeys }),
                keepalive: true,
              }).catch((err) => console.error('[cx-contact] Cleanup call failed', err));
            }
            alert(messages.submitFailed);
            submitButton?.removeAttribute('disabled');
            return;
          }
          submitButton?.removeAttribute('disabled');

          resetForm();
          openDialog();
        });
      }
    });
  }

  function hydrateInsightBoard(board) {
    if (board.dataset.cxInsightBound === 'true') return;
    board.dataset.cxInsightBound = 'true';

    const grid = board.querySelector('[data-cx-insight-grid]');
    if (!grid) return;

    const loadingEl = board.querySelector('[data-cx-insight-loading]');
    if (loadingEl) loadingEl.hidden = true;
    grid.classList.remove('is-loading');
    grid.hidden = false;

    setupInsightBoardInteractions(board);
  }

  function setupInsightBoardInteractions(board) {
      const grid = board.querySelector('[data-cx-insight-grid]');
      if (!grid) return;

      const allCards = [...grid.querySelectorAll('[data-cx-card]')];
      const chips = [...board.querySelectorAll('[data-cx-chip]')];
      const sortButtons = [...board.querySelectorAll('[data-cx-sort]')];
      const emptyEl = board.querySelector('[data-cx-empty]');
      const countEl = board.querySelector('[data-cx-result-count]');
      const moreWrap = board.querySelector('[data-cx-insight-more-wrap]');
      const moreBtn = board.querySelector('[data-cx-insight-more]');
      const moreCount = board.querySelector('[data-cx-insight-more-count]');
      const endEl = board.querySelector('[data-cx-insight-end]');
      const batch = Math.max(1, Number(board.dataset.perPage) || 12);

      const activeChip = chips.find((chip) => chip.classList.contains('is-active')) || chips[0];

      const state = {
        filter: activeChip ? activeChip.dataset.cxChip : 'contents',
        sort: 'latest',
        visible: batch,
      };

      const compareLatest = (a, b) =>
        Number(b.dataset.sortLatest || 0) - Number(a.dataset.sortLatest || 0);

      const comparePopular = (a, b) => {
        const av = a.dataset.sortPopular;
        const bv = b.dataset.sortPopular;
        const aHas = av != null && av !== '';
        const bHas = bv != null && bv !== '';
        if (aHas !== bHas) return aHas ? -1 : 1;
        if (aHas) {
          const an = Number(av);
          const bn = Number(bv);
          if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) {
            return an - bn;
          }
        }
        return compareLatest(a, b);
      };

      const getFiltered = () => {
        const filtered = state.filter === 'all'
          ? allCards.slice()
          : allCards.filter((card) => card.dataset.category === state.filter);
        filtered.sort(state.sort === 'popular' ? comparePopular : compareLatest);
        return filtered;
      };

      const render = () => {
        const filtered = getFiltered();

        const fragment = document.createDocumentFragment();
        filtered.forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);

        const visibleCount = Math.min(state.visible, filtered.length);
        const shownSet = new Set(filtered.slice(0, visibleCount));
        allCards.forEach((card) => {
          card.classList.toggle('is-hidden', !shownSet.has(card));
        });

        grid.classList.toggle('is-portfolio', state.filter === 'portfolio');

        if (countEl) {
          const tmpl = state.filter === 'all'
            ? countEl.dataset.tmplAll
            : state.filter === 'portfolio'
              ? countEl.dataset.tmplPortfolio
              : state.filter === 'press'
                ? countEl.dataset.tmplPress
                : countEl.dataset.tmplContents;
          countEl.textContent = (tmpl || '{N}').replace('{N}', String(filtered.length));
        }

        if (emptyEl) emptyEl.hidden = filtered.length > 0;

        const remaining = filtered.length - visibleCount;
        if (moreWrap) moreWrap.hidden = remaining <= 0;
        if (moreCount) moreCount.textContent = remaining > 0 ? '+' + Math.min(batch, remaining) : '';
        if (endEl) endEl.hidden = remaining > 0 || filtered.length <= batch;
      };

      chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          chips.forEach((c) => c.classList.remove('is-active'));
          chip.classList.add('is-active');
          state.filter = chip.dataset.cxChip;
          state.visible = batch;
          render();
        });
      });

      sortButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          let target = btn;
          if (btn.classList.contains('is-active') && sortButtons.length > 1) {
            const idx = sortButtons.indexOf(btn);
            target = sortButtons[(idx + 1) % sortButtons.length];
          }
          sortButtons.forEach((b) => b.classList.remove('is-active'));
          target.classList.add('is-active');
          state.sort = target.dataset.cxSort;
          state.visible = batch;
          render();
        });
      });

      moreBtn?.addEventListener('click', () => {
        state.visible += batch;
        render();
      });

      render();
  }


  function initInsightBoard(scope = document) {
    const boards = scope.matches?.('[data-cx-insight-board]')
      ? [scope]
      : [...scope.querySelectorAll('[data-cx-insight-board]')];
    boards.forEach((board) => {
      hydrateInsightBoard(board);
    });
  }

  function initInsightArticle(scope = document) {
    const roots = scope.matches?.('[data-cx-insight-article]')
      ? [scope]
      : [...scope.querySelectorAll('[data-cx-insight-article]')];

    roots.forEach((root) => {
      if (root.dataset.cxBound === 'true') return;
      root.dataset.cxBound = 'true';

      const sidebar = root.querySelector('[data-cx-recently-post]');
      const toggle = root.querySelector('[data-cx-recently-toggle]');
      if (!sidebar || !toggle) return;

      const setOpen = (open) => {
        sidebar.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      toggle.addEventListener('click', () => {
        setOpen(!sidebar.classList.contains('is-open'));
      });

      const mobileQuery = window.matchMedia('(max-width: 989px)');
      let lastY = window.scrollY;
      let autoCollapsed = false;

      const onScroll = () => {
        const y = window.scrollY;
        if (!mobileQuery.matches) {
          lastY = y;
          return;
        }
        if (!autoCollapsed && y > lastY + 24 && y > 80 && sidebar.classList.contains('is-open')) {
          setOpen(false);
          autoCollapsed = true;
        }
        if (y <= 8) autoCollapsed = false;
        lastY = y;
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      mobileQuery.addEventListener?.('change', (event) => {
        if (!event.matches) {
          setOpen(true);
          autoCollapsed = false;
        }
      });
    });
  }

  function initConcentrixTheme(scope = document) {
    scope.querySelectorAll('[data-cx-slider]').forEach((element) => new ConcentrixHero(element));
    initBrochureLinkContrast(scope);
    initLoadMore(scope);
    initContactShell(scope);
    initInsightBoard(scope);
    initInsightArticle(scope);
  }

  document.addEventListener('DOMContentLoaded', () => initConcentrixTheme(document));
  document.addEventListener('shopify:section:load', (event) => initConcentrixTheme(event.target));
})();
